import {ELO_LEVEL_BASE} from './const';
import {Storage} from './storage';

export const ANON_ELO_STORAGE_KEY = 'anon_elo_state_v1';
const ANON_ELO_HISTORY_ENTRY_COUNT = 50;
const K_VAL = 32;
const K_FADE = 80;
const K_USER_EXPERIENCE_DIVIDER = 400;

export type AnonymousProblemHistoryEntry = {
    id: number;
    imageUrl?: string;
    solved: boolean;
    durationSeconds: number;
    trialId?: number;
    rushId?: number;
};

type AnonymousEloState = {
    elo: number;
    eloHistory: number[];
    trialProblemHistory?: AnonymousProblemHistoryEntry[];
    lastSolve: number | null;
    problemSolved: boolean;
};

type ApplyAnonymousProblemSolveParams = {
    problemElo?: number | null;
    solved: boolean;
    hardStop: boolean;
    affectElo?: boolean;
    problemId?: number;
    imageUrl?: string;
    durationSeconds?: number;
    trialId?: number;
    rushId?: number;
};

const defaultState = (): AnonymousEloState => ({
    elo: ELO_LEVEL_BASE,
    eloHistory: [],
    trialProblemHistory: [],
    lastSolve: null,
    problemSolved: false,
});

const normalizeState = (state: unknown): AnonymousEloState => {
    const fallbackState = defaultState();

    if (!state || typeof state !== 'object') {
        return fallbackState;
    }

    const value = state as Partial<AnonymousEloState>;

    return {
        elo:
            typeof value.elo === 'number' && Number.isFinite(value.elo)
                ? value.elo
                : fallbackState.elo,
        eloHistory: Array.isArray(value.eloHistory)
            ? value.eloHistory.filter(
                  entry => typeof entry === 'number' && Number.isFinite(entry)
              )
            : [],
        trialProblemHistory: Array.isArray(value.trialProblemHistory)
            ? value.trialProblemHistory
            : [],
        lastSolve: typeof value.lastSolve === 'number' ? value.lastSolve : null,
        problemSolved: !!value.problemSolved,
    };
};

const calculateUserExperience = (
    problemElo: number,
    userElo: number,
    solved: boolean
) => {
    let result =
        1 / (1 + 10 ** ((problemElo - userElo) / K_USER_EXPERIENCE_DIVIDER));

    if (!solved) {
        result = 1 - result;
    }

    return result;
};

const calculateUserElo = (
    problemElo: number,
    userElo: number,
    solved: boolean
) => {
    const kUser = (K_VAL * K_FADE) / (K_FADE + Math.sqrt(1));

    return (
        userElo +
        (solved ? 1 : -1) *
            kUser *
            (1 - calculateUserExperience(problemElo, userElo, solved))
    );
};

export const getAnonymousEloState = (): AnonymousEloState =>
    normalizeState(Storage.getObjData(ANON_ELO_STORAGE_KEY));

const saveAnonymousEloState = (state: AnonymousEloState) => {
    Storage.storeObjData(ANON_ELO_STORAGE_KEY, state);
};

export const getAnonymousEloHistory = (): number[] =>
    getAnonymousEloState().eloHistory;

export const applyAnonymousProblemSolve = ({
    problemElo,
    solved,
    hardStop,
    affectElo = true,
    problemId,
    imageUrl,
    durationSeconds = 0,
    trialId,
    rushId,
}: ApplyAnonymousProblemSolveParams): AnonymousEloState => {
    const state = getAnonymousEloState();

    if (!hardStop) {
        return state;
    }

    if (problemElo === null || !Number.isFinite(problemElo)) {
        return state;
    }

    let updatedElo = state.elo;
    let updatedEloHistory = state.eloHistory;
    let updatedProblemHistory = state.trialProblemHistory || [];

    if (affectElo) {
        if (problemElo != null) {
            updatedElo = calculateUserElo(problemElo, state.elo, solved);
        }
        updatedEloHistory = [...state.eloHistory, Math.trunc(updatedElo)].slice(
            -ANON_ELO_HISTORY_ENTRY_COUNT
        );
    }

    if (problemId) {
        updatedProblemHistory = [
            ...updatedProblemHistory,
            {id: problemId, imageUrl, solved, durationSeconds, trialId, rushId},
        ].slice(-ANON_ELO_HISTORY_ENTRY_COUNT);
    }

    const nextState: AnonymousEloState = {
        elo: updatedElo,
        eloHistory: updatedEloHistory,
        trialProblemHistory: updatedProblemHistory,
        lastSolve: Math.floor(Date.now() / 1000),
        problemSolved: solved || state.problemSolved,
    };

    saveAnonymousEloState(nextState);

    return nextState;
};
